import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import ContentLayoutHeader from "../../components/layouts/ContentLayoutHeader.layout"
import Card from "../../components/atoms/Card.atom"
import Divider from "../../components/atoms/Divider.atom"
import FieldContainer from "../../components/layouts/containers/FieldContainer.container"
import InputTextForm from "../../components/molecules/InputTextForm.molecule"
import InputNumberForm from "../../components/molecules/InputNumberForm.molecule"
import DropdownInput from "../../components/molecules/DropdownInput.molecule"
import InputMaskForm from "../../components/molecules/InputMaskForm.molecule"
import InputCalendarForm from "../../components/molecules/InputCalendarForm.molecule"
import PrimaryButton from "../../components/molecules/buttons/PrimaryButton.molecule"
import { AppRoutes } from "../../consts/appRoutes.consts"
import {
  FORM_TEAM_MEMBER_HOLIDAY_SCHEMAS_IDS_WITH_ACCORDED_DAYS,
  TEAM_MEMBER_INFO_FORM_INITIAL_DATA_STATE,
  TEAM_MEMBER_INFO_FORM_INITIAL_VALIDATION_STATE,
} from "../../consts/modules/teamMembers.consts"
import { RANGES } from "../../consts/CompoundRangeDatePicker"
import {
  validateFieldValue,
  checkAllValidations,
  isNotEmpty,
  isANumberGreaterThanOrEqualZero,
} from "../../utils/validations.utils"
import {
  createTeamMember,
  getTeamAreas,
  getHolidaySchemas,
} from "../../services/teamMember.service"
import useToast from "../../hooks/useToast.hook"
import _ from "lodash"
import { CREATING_MEMBER_HOLIDAY_SCHEMA_ID } from "../../consts/modules/vacations.consts"
import {getModifiedDateTime} from "../../utils/dateTime.utils"

const TEAM_MEMBER_ID_HOLIDAY_SCHEMA_INPUT_NAME = "teamMemberIdHolidaySchema"

const TeamMemberCreateForm = () => {
  const [formData, setFormData] = useState(
    _.cloneDeep(TEAM_MEMBER_INFO_FORM_INITIAL_DATA_STATE)
  )
  const [validations, setValidations] = useState(
    _.cloneDeep(TEAM_MEMBER_INFO_FORM_INITIAL_VALIDATION_STATE)
  )
  const [teamAreas, setTeamAreas] = useState([])
  const [holidaySchemas, setHolidaySchemas] = useState([])
  const [disabledSaveButton, setDisabledSaveButton] = useState(true)

  const navigate = useNavigate()
  const { setErrorMessage } = useToast()

  useEffect(() => {
    getTeamAreaInfo()
    getHolidaySchemaInfo()
  }, [])

  const getTeamAreaInfo = async () => {
    const response = await getTeamAreas()
    if (!response.success) {
      throw new Error(response.message)
    }
    setTeamAreas(response.result)
  }

  const getHolidaySchemaInfo = async () => {
    const response = await getHolidaySchemas()
    if (!response.success) {
      throw new Error(response.message)
    }
    setHolidaySchemas(response.result)
  }

  useEffect(() => {
    const validationsError = checkAllValidations(validations)
    setDisabledSaveButton(validationsError)
  }, [formData, validations])

  function onTeamMemberIdHolidaySchemaChange(newHolidaySchema) {
    if (newHolidaySchema === CREATING_MEMBER_HOLIDAY_SCHEMA_ID.INTERNATIONAL) {
      setValidations((prev) => ({
        ...prev,
        teamMemberAccordedDays: {
          ...prev.teamMemberAccordedDays,
          value: 1,
          errors: [],
          typeValidations: [isNotEmpty, isANumberGreaterThanOrEqualZero],
        },
      }))
      setFormData((pre) => ({
        ...pre,
        teamMemberAccordedDays: 1,
      }))
    } else {
      setFormData((pre) => ({
        ...pre,
        teamMemberAccordedDays: 0,
      }))
      setValidations((prev) => ({
        ...prev,
        teamMemberAccordedDays: {
          ...prev.teamMemberAccordedDays,
          value: 0,
          errors: [],
          typeValidations: [],
        },
      }))
    }
  }

  const handleChange = ({ target: { name, value } }) => {
    setFormData({ ...formData, [name]: value })
    setValidations(validateFieldValue(validations, name, value))
    if (name === TEAM_MEMBER_ID_HOLIDAY_SCHEMA_INPUT_NAME) {
      onTeamMemberIdHolidaySchemaChange(value)
    }
  }

  const handleSubmitProject = async () => {
    try {
      const response = await createTeamMember({
        teamMemberAccordedDays: formData.teamMemberAccordedDays,
        teamMemberIdArea: formData.teamMemberIdArea,
        teamMemberIdHolidaySchema: formData.teamMemberIdHolidaySchema,
        teamMemberJobTitle: formData.teamMemberJobTitle,
        teamMemberNameTag: formData.teamMemberNameTag,
        userAlias: formData.userAlias,
        userBirthday: formData.userBirthday.toISODate(),
        userEmail: formData.userEmail,
        userFirstName: formData.userFirstName,
        userLastName: formData.userLastName,
        userPhone: formData.userPhone,
        userWorkAnniversay: formData.userWorkAnniversay.toISODate(),
      })
      if (!response.success) {
        throw new Error(response.message)
      }
      navigate(AppRoutes.integrantes.index)
    } catch (error) {
      setErrorMessage({
        title: "Error",
        message: error.message,
      })
      setDisabledSaveButton(false)
    }
  }

  return (
    <div id="team-member-create-form">
      <ContentLayoutHeader title={"Creación de integrante"} goBack />
      <Card className="p-1 shadow-none mb-3">
        <div className="flex flex-column align-items-flex-start justify-content-between w-full">
          <h3 className="font-cairo font-semibold text-3xl">
            Datos personales
          </h3>
          <div className="flex flex-row justify-content-between w-full mb-3">
            <FieldContainer col={6}>
              <InputTextForm
                label="Nombre"
                name="userFirstName"
                onChange={handleChange}
                value={formData.userFirstName}
                error={validations.userFirstName.errors}
              />
            </FieldContainer>
            <FieldContainer col={6}>
              <InputTextForm
                label="Apellidos"
                name="userLastName"
                onChange={handleChange}
                value={formData.userLastName}
                error={validations.userLastName.errors}
              />
            </FieldContainer>
          </div>
          <div className="flex flex-row justify-content-between w-full mb-3">
            <FieldContainer col={6}>
              <InputTextForm
                label="Código"
                name="teamMemberNameTag"
                onChange={handleChange}
                value={formData.teamMemberNameTag}
                error={validations.teamMemberNameTag.errors}
              />
            </FieldContainer>
            <FieldContainer col={6}>
              <InputTextForm
                label="Alias"
                name="userAlias"
                onChange={handleChange}
                value={formData.userAlias}
                error={validations.userAlias.errors}
              />
            </FieldContainer>
          </div>
          <div className="flex flex-row justify-content-between w-full">
            <FieldContainer col={6}>
              <InputCalendarForm
                label="Cumpleaños"
                name="userBirthday"
                value={formData.userBirthday}
                period={RANGES.DAY}
                inputReadOnly
                allowClear={false}
                format="DD/MM/YYYY"
                onChange={(date) => {
                  handleChange({
                    target: { name: "userBirthday", value: date },
                  })
                }}
                maxDate={getModifiedDateTime({endOf: "day"})}
                error={validations.userBirthday.errors}
              />
            </FieldContainer>
          </div>
        </div>
        <Divider className="mt-0 mb-3" stroke="sm" />
        <div className="flex flex-column align-items-flex-start justify-content-between w-full">
          <h3 className="font-cairo font-semibold text-3xl">
            Datos de contacto
          </h3>
          <div className="flex flex-row justify-content-between w-full">
            <FieldContainer col={6}>
              <InputTextForm
                label="Correo electrónico"
                name="userEmail"
                onChange={handleChange}
                value={formData.userEmail}
                error={validations.userEmail.errors}
              />
            </FieldContainer>
            <FieldContainer col={6}>
              <InputMaskForm
                label="Teléfono"
                name="userPhone"
                mask="+999 999 999 9999"
                placeholder="+052 111 222 3333"
                onChange={handleChange}
                value={formData.userPhone}
                error={validations.userPhone.errors}
              />
            </FieldContainer>
          </div>
        </div>
        <Divider className="mt-0 mb-3" stroke="sm" />
        <div className="flex flex-column align-items-flex-start justify-content-between w-full">
          <h3 className="font-cairo font-semibold text-3xl">
            Información laboral
          </h3>
          <div className="flex flex-row justify-content-between w-full mb-3">
            <FieldContainer col={6}>
              <DropdownInput
                className="modules-dropdown"
                label="Área"
                name="teamMemberIdArea"
                options={teamAreas}
                optionLabel="name"
                optionValue="id"
                value={formData.teamMemberIdArea}
                errors={validations.teamMemberIdArea.errors}
                onChange={handleChange}
                itemTemplate={(option) => {
                  return (
                    <div className="flex gap-2 align-items-center module-item">
                      <span className="flex-1">{option.name}</span>
                    </div>
                  )
                }}
              />
            </FieldContainer>
            <FieldContainer col={6}>
              <InputTextForm
                label="Puesto"
                name="teamMemberJobTitle"
                onChange={handleChange}
                value={formData.teamMemberJobTitle}
                error={validations.teamMemberJobTitle.errors}
              />
            </FieldContainer>
          </div>
          <div
            className={`flex flex-row justify-content-between w-full${FORM_TEAM_MEMBER_HOLIDAY_SCHEMAS_IDS_WITH_ACCORDED_DAYS.includes(formData.teamMemberIdHolidaySchema) ? " mb-3" : ""}`}
          >
            <FieldContainer col={6}>
              <InputCalendarForm
                label="Fecha de ingreso"
                name="userWorkAnniversay"
                value={formData.userWorkAnniversay}
                period={RANGES.DAY}
                inputReadOnly
                allowClear={false}
                format="DD/MM/YYYY"
                onChange={(date) => {
                  handleChange({
                    target: { name: "userWorkAnniversay", value: date },
                  })
                }}
                maxDate={getModifiedDateTime({endOf: "day"})}
                error={validations.userWorkAnniversay.errors}
              />
            </FieldContainer>
            <FieldContainer col={6}>
              <DropdownInput
                className="modules-dropdown"
                label="Esquema de vacaciones"
                name={TEAM_MEMBER_ID_HOLIDAY_SCHEMA_INPUT_NAME}
                options={holidaySchemas}
                optionLabel="name"
                optionValue="id"
                value={formData.teamMemberIdHolidaySchema}
                errors={validations.teamMemberIdHolidaySchema.errors}
                onChange={handleChange}
                itemTemplate={(option) => {
                  return (
                    <div className="flex gap-2 align-items-center module-item">
                      <span className="flex-1">{option.name}</span>
                    </div>
                  )
                }}
              />
            </FieldContainer>
          </div>
          {(
            FORM_TEAM_MEMBER_HOLIDAY_SCHEMAS_IDS_WITH_ACCORDED_DAYS.includes(
              formData.teamMemberIdHolidaySchema
            )
          ) ?
            <div className="flex flex-row justify-content-between w-full">
              <FieldContainer col={6}>
                <InputNumberForm
                  min={1}
                  label="Días de vacaciones al año"
                  name="teamMemberAccordedDays"
                  onChange={(days) => {
                    handleChange({
                      target: {
                        name: "teamMemberAccordedDays",
                        value: days.value,
                      },
                    })
                  }}
                  value={formData.teamMemberAccordedDays}
                  error={validations.teamMemberAccordedDays.errors}
                />
              </FieldContainer>
            </div>
          : null}
        </div>
      </Card>
      <div className="flex align-items-center justify-content-end flex-row gap-3">
        <PrimaryButton
          icon="pi pi-check"
          className="button__primary"
          label="Guardar"
          onClick={() => {
            setDisabledSaveButton(true)
            handleSubmitProject()
          }}
          disabled={disabledSaveButton}
        />
      </div>
    </div>
  )
}

export default TeamMemberCreateForm
